import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withModifiers as _withModifiers, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "w-full px-2 sm:px-6 py-4" }
const _hoisted_2 = { class: "bg-white rounded-lg border border-gray-200 shadow-sm p-6" }
const _hoisted_3 = { class: "flex items-center" }
const _hoisted_4 = { class: "w-2/3 flex items-center gap-3" }
const _hoisted_5 = { class: "flex items-start" }
const _hoisted_6 = { class: "w-2/3" }
const _hoisted_7 = { class: "flex gap-3" }
const _hoisted_8 = ["disabled"]
const _hoisted_9 = {
  key: 0,
  class: "animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500",
  xmlns: "http://www.w3.org/2000/svg",
  fill: "none",
  viewBox: "0 0 24 24"
}
const _hoisted_10 = ["disabled"]
const _hoisted_11 = {
  key: 0,
  class: "animate-spin -ml-1 mr-2 h-4 w-4 text-gray-500",
  xmlns: "http://www.w3.org/2000/svg",
  fill: "none",
  viewBox: "0 0 24 24"
}
const _hoisted_12 = { class: "flex justify-end pt-4" }

import { ref, reactive } from 'vue'
import { PageHeader, ActionButton } from '@/components'
import { useNotification } from '@/composables/useNotification'
import { sendInvitations, getEmailPreview } from '@/api/invitations'
import { useAuthStore } from '@/stores/auth'
import type { EmailForm, InvitationResult } from '@/types'

// UI State

export default /*@__PURE__*/_defineComponent({
  __name: 'PromotionalEmailView',
  setup(__props) {

const isSending = ref(false)
const isLoadingPreview = ref(false)
const fileInput = ref<HTMLInputElement>()

// Notifications
const { success, error } = useNotification()

// Auth store
const authStore = useAuthStore()

// Form data
const emailForm = reactive<EmailForm>({
  recipients: null
})
const handleFileUpload = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    emailForm.recipients = target.files[0]
  }
}

const readFileContent = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      const content = e.target?.result as string
      resolve(content)
    }
    reader.onerror = () => reject(new Error('Erreur lors de la lecture du fichier'))
    reader.readAsText(file, 'UTF-8')
  })
}

const handleSubmit = async () => {
  try {
    isSending.value = true
    
    // Vérifier l'authentification
    if (!authStore.isAuthenticated) {
      error('Vous devez être connecté pour envoyer des emails')
      return
    }
    
    // Vérifier qu'un fichier CSV est sélectionné
    if (!emailForm.recipients) {
      error('Veuillez sélectionner un fichier CSV')
      return
    }
    
    // Lire le contenu du fichier CSV
    const csvContent = await readFileContent(emailForm.recipients)
    
    // Envoyer les invitations via l'API
    const result: InvitationResult = await sendInvitations(csvContent)
    
    // Afficher le résultat
    if (result.success) {
      success(`${result.successful} emails d'invitation envoyés avec succès !`)
    } else {
      error(`${result.failed} erreurs sur ${result.totalProcessed} emails`)
    }
    
    // Reset form
    emailForm.recipients = null
    if (fileInput.value) {
      fileInput.value.value = ''
    }
    
  } catch (err) {
    error('Erreur lors de l\'envoi des emails d\'invitation')
  } finally {
    isSending.value = false
  }
}

const downloadTemplate = () => {
  try {
    const a = document.createElement('a')
    a.href = '/templateEmail.csv'
    a.download = 'templateEmail.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    
    success('Template CSV téléchargé avec succès !')
  } catch (err) {
    error('Erreur lors du téléchargement du template CSV')
  }
}

const openPreview = async (language: string) => {
  try {
    isLoadingPreview.value = true
    
    if (!authStore.isAuthenticated) {
      error('Vous devez être connecté pour voir l\'aperçu')
      return
    }
    
    const htmlContent = await getEmailPreview(language)
    const previewWindow = window.open('', '_blank', 'width=900,height=700,scrollbars=yes,resizable=yes')
    
    if (!previewWindow) {
      error('Impossible d\'ouvrir l\'aperçu. Vérifiez que les pop-ups sont autorisés.')
      return
    }
    
    previewWindow.document.write(`
      <!DOCTYPE html>
      <html lang="${language}">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Aperçu Email - Efashion Paris</title>
        <style>
          body { margin: 0; padding: 20px; background-color: #f5f5f5; font-family: Arial, sans-serif; }
          .preview-container { max-width: 800px; margin: 0 auto; background: white; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); overflow: hidden; }
        </style>
      </head>
      <body>
        <div class="preview-container">
          ${htmlContent}
        </div>
      </body>
      </html>
    `)
    previewWindow.document.close()
    
  } catch (err) {
    error('Erreur lors de l\'ouverture de l\'aperçu')
  } finally {
    isLoadingPreview.value = false
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_unref(PageHeader), {
      title: "Mails d'invitation d'acheteurs",
      subtitle: "Envoyez des emails promotionnels à vos acheteurs",
      class: "mb-0"
    }),
    _createElementVNode("div", _hoisted_1, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("form", {
          onSubmit: _withModifiers(handleSubmit, ["prevent"]),
          class: "space-y-6"
        }, [
          _cache[6] || (_cache[6] = _createElementVNode("div", { class: "flex items-center" }, [
            _createElementVNode("label", { class: "w-1/3 text-sm font-medium text-gray-700" }, "Expéditeur"),
            _createElementVNode("div", { class: "w-2/3" }, [
              _createElementVNode("input", {
                type: "email",
                class: "w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed",
                value: "service@efashion-paris.com",
                readonly: ""
              })
            ])
          ], -1)),
          _createElementVNode("div", _hoisted_3, [
            _cache[2] || (_cache[2] = _createElementVNode("label", { class: "w-1/3 text-sm font-medium text-gray-700" }, "Destinataire (CSV)", -1)),
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("input", {
                ref_key: "fileInput",
                ref: fileInput,
                type: "file",
                accept: ".csv",
                onChange: handleFileUpload,
                class: "flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              }, null, 544),
              _createVNode(_unref(ActionButton), {
                label: "Demo CSV",
                variant: "secondary",
                onClick: downloadTemplate
              })
            ])
          ]),
          _cache[7] || (_cache[7] = _createElementVNode("div", { class: "flex items-center" }, [
            _createElementVNode("label", { class: "w-1/3 text-sm font-medium text-gray-700" }, "Objet"),
            _createElementVNode("div", { class: "w-2/3" }, [
              _createElementVNode("input", {
                type: "text",
                class: "w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 cursor-not-allowed",
                value: "Commander maintenant en ligne !",
                readonly: ""
              })
            ])
          ], -1)),
          _createElementVNode("div", _hoisted_5, [
            _cache[5] || (_cache[5] = _createElementVNode("label", { class: "w-1/3 text-sm font-medium text-gray-700 mt-2" }, "Aperçu", -1)),
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("button", {
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (openPreview('fr'))),
                  disabled: isLoadingPreview.value,
                  class: "inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                }, [
                  (isLoadingPreview.value)
                    ? (_openBlock(), _createElementBlock("svg", _hoisted_9, _cache[3] || (_cache[3] = [
                        _createElementVNode("circle", {
                          class: "opacity-25",
                          cx: "12",
                          cy: "12",
                          r: "10",
                          stroke: "currentColor",
                          "stroke-width": "4"
                        }, null, -1),
                        _createElementVNode("path", {
                          class: "opacity-75",
                          fill: "currentColor",
                          d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        }, null, -1)
                      ])))
                    : _createCommentVNode("", true),
                  _createTextVNode(" " + _toDisplayString(isLoadingPreview.value ? 'Chargement...' : 'Aperçu FR'), 1)
                ], 8, _hoisted_8),
                _createElementVNode("button", {
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (openPreview('en'))),
                  disabled: isLoadingPreview.value,
                  class: "inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                }, [
                  (isLoadingPreview.value)
                    ? (_openBlock(), _createElementBlock("svg", _hoisted_11, _cache[4] || (_cache[4] = [
                        _createElementVNode("circle", {
                          class: "opacity-25",
                          cx: "12",
                          cy: "12",
                          r: "10",
                          stroke: "currentColor",
                          "stroke-width": "4"
                        }, null, -1),
                        _createElementVNode("path", {
                          class: "opacity-75",
                          fill: "currentColor",
                          d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        }, null, -1)
                      ])))
                    : _createCommentVNode("", true),
                  _createTextVNode(" " + _toDisplayString(isLoadingPreview.value ? 'Chargement...' : 'Aperçu EN'), 1)
                ], 8, _hoisted_10)
              ])
            ])
          ]),
          _createElementVNode("div", _hoisted_12, [
            _createVNode(_unref(ActionButton), {
              label: isSending.value ? 'Envoi en cours...' : 'Envoyer',
              variant: "primary",
              type: "submit",
              loading: isSending.value,
              disabled: !emailForm.recipients || isSending.value
            }, null, 8, ["label", "loading", "disabled"])
          ])
        ], 32)
      ])
    ])
  ], 64))
}
}

})